/* global React */
import { Grid } from '@mui/material'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Text from 'ui/Element/Text'
import Icon from 'ui/Element/Icon'

export interface SystemMessageType {
  /** unique identifier for the system message type */
  key: string
  /** the message text */
  value?: string
  /** the icon to display */
  icon?: string
  /** the icon and text color */
  color?: string
}

interface SystemProps {
  /** the system message to display */
  message: SystemMessageType
}

/**
 * Returns the System element's current theme styles.
 *
 * @param theme  the application's current theme
 * @returns      the System element's styles
 */
const styles = (theme: any) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  message: {
    color: theme.palette.text.light,
    fontWeight: 500,
    textAlign: 'center',
  },
  icon: {
    paddingBottom: theme.spacing(1),
  },
  ...theme.custom.systemMessage,
})

/**
 * System Message Item
 *
 * Message item used to display a system message.
 *
 * @param props         the element's properties
 * @param props.message the message object
 * @returns             the new SystemMessage element
 */
const SystemMessage = ({ message }: SystemProps) => {
  const classes = useStyles(styles)() as Record<string, string>

  return (
    <Grid
      item
      className={classes.root}
    >
      <Icon
        name={message.icon}
        color={message.color}
        className={classes.icon}
        size={66}
      />
      <Text
        className={classes.message}
        variant={'body1'}
      >
        {message.value}
      </Text>
    </Grid>
  )
}

export default useMemoRef(SystemMessage, (props: SystemProps) => [props.message])
